import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import {
  addMediaElementAction,
  removeMediaElementAction,
  setSlotLayoutAction,
  updateMediaElementAction,
} from 'editor/src/store/design/slice';
import { Rect, SlotFileSet, SlotFrame, SlotLayout } from 'editor/src/store/design/types';
import buildSlotPictureElement from 'editor/src/store/design/util/slotLayout/buildSlotPictureElement';
import computeSlotFrames, {
  getMismatchedCounts,
  getUsedSlotFormats,
} from 'editor/src/store/design/util/slotLayout/computeSlotFrames';
import getSlotFileSetOptionIndexes from 'editor/src/store/design/util/slotLayout/getSlotFileSetOptionIndexes';
import {
  getSlotFrameElements,
  getSlotLayoutElements,
  getSlotTemplateFrame,
  isHandPlacedSlotElement,
} from 'editor/src/store/design/util/slotLayout/slotLayoutElements';
import { slotLinkGroupId } from 'editor/src/store/design/util/slotLayout/slotLayoutIds';
import { getSlotTransform, transformSlotGeometry } from 'editor/src/store/design/util/slotLayout/transformSlotElement';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import { getElementCollectionAssets } from 'editor/src/store/plugins/utils/advPersoUtils';

import { elementUuids } from 'editor/src/util/uuids';

import { getLinkedCollectionElements } from '../util/collectionLink';

import applySlotTemplateOperation from './applySlotTemplateOperation';
import attachSlotElementToProduct from './attachSlotElementToProduct';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

export interface SlotLayoutGridUpdate {
  rows: number[][];
  area: Rect;
  gap: number;
  /** With folders for formats the table did not use before; the others are already wired in. */
  fileSets?: SlotFileSet[];
}

const sameRect = (a: Rect, b: Rect) => a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

/**
 * Re-cuts the grid after the author changes the rows, the area or the gap. Frames that keep
 * their place keep their layers; frames that move take their layers along; frames that
 * disappear take theirs away; new frames get picture layers and, where the format already
 * has a designed template, a copy of it.
 */
const updateSlotLayoutRowsOperation =
  (spreadIndex: number, layoutId: string, update: SlotLayoutGridUpdate) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const spread = state.design.designData?.spreads[spreadIndex];
    const layout = spread?.slotLayouts?.find((item) => item.id === layoutId);
    if (!spread || !layout || update.rows.length !== layout.maxProducts || getMismatchedCounts(update.rows).length) {
      return;
    }

    const frames = computeSlotFrames(update.rows, update.area, update.gap);
    const next = new Map(frames.map((frame) => [frame.id, frame]));
    const previous = new Map(layout.frames.map((frame) => [frame.id, frame]));
    const { rows, area, gap, fileSets = layout.fileSets } = update;
    const nextLayout: SlotLayout = { ...layout, rows, area, gap, fileSets, frames };
    // Formats with a designed template among the frames that survive: those get their new
    // frames filled; a designed frame the new table drops takes its design with it.
    const survivingDesign = (spread.pages[0].groups.media ?? []).filter(
      (element) =>
        isHandPlacedSlotElement(element) &&
        element.slotFrame?.layoutId === layoutId &&
        next.has(element.slotFrame.frameId),
    );
    const templatesWithDesign = new Set(
      getUsedSlotFormats(update.rows).filter((columns) => {
        const template = getSlotTemplateFrame(nextLayout, columns, survivingDesign);
        return template && survivingDesign.some((element) => element.slotFrame?.frameId === template.id);
      }),
    );

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Product layout grid'));

      layout.frames
        .filter((frame) => !next.has(frame.id))
        .flatMap((frame) => getSlotFrameElements(spread, spreadIndex, layoutId, frame.id))
        .sort((a, b) => b.address.elementIndex - a.address.elementIndex)
        .forEach(({ address }) => dispatch(removeMediaElementAction(address)));

      // A copy whose template layer went with a dropped frame has nothing left to stand for.
      const afterDrop = getState().design.designData!.spreads[spreadIndex];
      const kept = new Set((afterDrop.pages[0].groups.media ?? []).map((element) => element.uuid));
      getSlotLayoutElements(afterDrop, spreadIndex, layoutId)
        .filter(({ element }) => element.slotFrame?.sourceUuid !== undefined && !kept.has(element.slotFrame.sourceUuid))
        .sort((a, b) => b.address.elementIndex - a.address.elementIndex)
        .forEach(({ address }) => dispatch(removeMediaElementAction(address)));

      frames.forEach((frame) => {
        const before = previous.get(frame.id);
        if (before && !sameRect(before, frame)) {
          const transform = getSlotTransform(before, frame);
          getSlotFrameElements(
            getState().design.designData!.spreads[spreadIndex],
            spreadIndex,
            layoutId,
            frame.id,
          ).forEach(({ element, address }) =>
            dispatch(
              updateMediaElementAction({
                elementAddress: address,
                elementUpdate: transformSlotGeometry(element, transform),
              }),
            ),
          );
        }
        if (!before) {
          dispatch(addPictureLayers(spreadIndex, nextLayout, frame));
        }
      });

      dispatch(setSlotLayoutAction({ spreadIndex, layout: nextLayout }));

      getUsedSlotFormats(update.rows)
        .filter(
          (columns) =>
            templatesWithDesign.has(columns) && frames.some((f) => f.columns === columns && !previous.has(f.id)),
        )
        .forEach((columns) => dispatch(applySlotTemplateOperation(spreadIndex, layoutId, columns, false)));

      dispatch(updateHiddenElementsOperation(spreadIndex));
    });
  };

const addPictureLayers =
  (spreadIndex: number, layout: SlotLayout, frame: SlotFrame) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const { collections, design } = getState();
    layout.fileSets.forEach((fileSet) => {
      const folderRef = fileSet.folders[frame.columns];
      // The product's other frames already show its pick; linked layers follow by position.
      const linked = getLinkedCollectionElements(
        design.designData!,
        slotLinkGroupId(layout.id, fileSet.id, frame.product),
      );
      const sibling = linked.find((item) => collections.folders[item.collectionInfo.folderId]);
      const assetIndex = sibling
        ? getElementCollectionAssets(
            collections.folders[sibling.collectionInfo.folderId],
            sibling.collectionInfo,
          ).findIndex((asset) => asset.id === sibling.element.imageId)
        : -1;
      const element = buildSlotPictureElement({
        layoutId: layout.id,
        frame,
        fileSet,
        folder: folderRef ? collections.folders[folderRef.folderId] : undefined,
        uuid: elementUuids.generate(),
        name: `${fileSet.name} ${frame.product}`,
        assetIndex: assetIndex === -1 ? undefined : assetIndex,
      });
      if (element) {
        dispatch(addMediaElementAction({ spreadIndex, pageIndex: 0, element }));
        attachSlotElementToProduct(
          dispatch,
          getState,
          spreadIndex,
          layout,
          frame.product,
          element,
          getSlotFileSetOptionIndexes(fileSet, layout.productOptions),
        );
      }
    });
  };

export default updateSlotLayoutRowsOperation;
